import { Injectable, NgZone } from '@angular/core';
import { AngularFirestoreCollection } from '@angular/fire/firestore';
import { NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, throttleTime } from 'rxjs/operators';

import firebase from 'firebase/app';
import 'firebase/functions';

import { ChangePasswordError, LoginError, LoginErrorCode, ResendConfirmationEmailError, ResetPasswordError, SMSTokenError } from '../../errors';
import { AuthenticationConfiguration, MultifactorAuthentication, Role, SessionUser, TokenValidity } from '../../models';
import { AuthActions, State } from '../../store';

import { AuthenticationService, RecaptchaVerifierSize, RecaptchaVerifierTheme } from '../authentication.service';
import { FirestoreService } from './firestore.service';
import { HttpRequestRetryPolicyService } from '../http/http-request-retry-policy.service';
import { HttpRequestService } from '../http/http-request.service';
import { LocalStorageService } from '../local-storage.service';
import { MfaAuthService } from '../mfa.auth.service';

import { environment } from 'src/environments/environment';

// Export Firestore Timestamp type
export class FirestoreTimestamp extends firebase.firestore.Timestamp {}

interface SessionTokensDto {
    getSessionToken?: () => Promise<string>;
}

interface SessionUserDTO extends SessionTokensDto {
    canTakeControlOfEscalatedTickets?: boolean;
    createdAt: FirestoreTimestamp;
    deleted: boolean;
    email: string;
    id?: string;
    inviteAccepted: boolean;
    isInternal: boolean;
    lastAccess: FirestoreTimestamp;
    name: string;
    passwordChangeRequired: boolean;
    phone?: string;
    photo: string;
    roleId: string;
    shouldNotRegisterMetrics?: boolean;
    statusId: string;
    tagsIds: string[];
    tenantId: string;
}

/**
 * Authentication service using firestore authentication.
 *
 * @export
 */
@Injectable({
    providedIn: 'root',
})
export class FirestoreAuthenticationService implements AuthenticationService {
    private _user: SessionUser;
    private _fbUser: firebase.User;

    private userSubject: Subject<SessionUser>;
    private isAuthenticatedSubject: Subject<boolean>;
    private keyboardSubject: Subject<KeyboardEvent> = new BehaviorSubject<KeyboardEvent>(null);
    private mouseSubject: Subject<MouseEvent> = new BehaviorSubject<MouseEvent>(null);
    private userActivitySubscription: Subscription;
    private startSessionUserUpdateSubscription: Subscription;

    private recaptchaVerifier: firebase.auth.RecaptchaVerifier; // reference to the recaptcha-verifier
    private recaptchaVerifierStatusSubject: Subject<boolean>; // subject to save the state of the recaptcha-verifier
    private resolver: any; // multi-factor-auth resolver
    private verificationId: string; // verificationId to be used with the verificationCode
    private phoneAuthProvider: firebase.auth.PhoneAuthProvider; // instance of a phone-provider used by multi-factor auth

    private authenticationConfigurationSubscription: Subscription;
    private shouldClearUserSessionSubject: Subject<boolean>;
    private userSessionTimeout: number = undefined;
    private multifactorAuthentication: MultifactorAuthentication;
    private hasVerified: boolean = false;
    private isLogin: boolean = false;
    private isFirstLoading: boolean = true;
    private showAfterLoginStatusPrompt: boolean = false;
    private showAfterLoginStatusPromptSubject: Subject<boolean>;

    private currentTicketsView: 'escalatable' | 'non-escalatable';
    private ticketsViewSubscription: Subscription;

    /** the current authenticated user */
    get user(): SessionUser {
        return this._user;
    }

    /** the firestore users collection */
    private get _usersCollection(): AngularFirestoreCollection<SessionUserDTO> {
        return this.firestoreService.collection<SessionUserDTO>('users');
    }

    constructor(
        private firestoreService: FirestoreService,
        private httpRetryPolicyService: HttpRequestRetryPolicyService,
        private httpService: HttpRequestService,
        private localStorage: LocalStorageService,
        private mfaAuthService: MfaAuthService,
        private ngZone: NgZone,
        private router: Router,
        private store: Store<State>,
    ) {
        this.userSubject = new ReplaySubject<SessionUser>(1);
        this.isAuthenticatedSubject = new ReplaySubject<boolean>(1);
        this.recaptchaVerifierStatusSubject = new ReplaySubject<boolean>(1);
        this.shouldClearUserSessionSubject = new ReplaySubject<boolean>(1);
        this.showAfterLoginStatusPromptSubject = new ReplaySubject<boolean>(1);

        this.firestoreService.auth.authState.subscribe((firestoreUser: firebase.User) => {
            if (firestoreUser) {
                this.getSessionUser(firestoreUser).then((sessionUser: SessionUser) => {
                    this.hasVerified = this.localStorage.get<boolean>(this.hasVerifiedFlag(), true);

                    if (this.hasVerified) {
                        this.authenticationSuccess(sessionUser, firestoreUser);
                    } else {
                        this.authenticationUnsuccess();
                    }
                });
            } else {
                this.authenticationUnsuccess();
            }
        });

        this.router.events.subscribe((e) => {
            if (this._user && e instanceof NavigationEnd) {
                const ne = e as NavigationEnd;
                this.updateUserStatusActivityDetected(ne.url);
            }
        });
    }

    /** Method that provides an observable with the changes of the session user. */
    sessionUserChanges(): Observable<SessionUser> {
        return this.userSubject.asObservable();
    }

    /** Used to create the hasVerified flag from projectId */
    private hasVerifiedFlag(): string {
        return 'hasVerified' + environment.firebaseConfig.projectId;
    }

    /** Used to complete a successful authentication. */
    private authenticationSuccess(sessionUser: SessionUser, firestoreUser: firebase.User): void {
        this._user = sessionUser;
        this.userSubject.next(this._user);
        this.startSessionUserUpdate(firestoreUser);
        this.isAuthenticatedSubject.next(true);
        this.firestoreService.cloudFunction('updateUserLastAccess')({ userId: sessionUser.id });
        this.watchUserActivity();
        this.store.dispatch(AuthActions.updateSessionUser({ sessionUser }));

        if (this.isLogin && sessionUser.statusId !== environment.operatorLoggedInStatuses) {
            this.updateUserStatus(sessionUser.id, environment.operatorLoggedInStatuses, !sessionUser?.shouldNotRegisterMetrics)
                .then(() => {
                    this.isLogin = false;
                    this.updateLoginStatusPrompt(true);
                });
        } else {
            this.fetchLoginStatusPrompt();
        }

        if (!this.authenticationConfigurationSubscription) {
            this.authenticationConfigurationSubscription = this.firestoreService
                .tenantDependentCollection(this.user.tenantId, 'configurations')
                .doc<AuthenticationConfiguration>('authentication')
                .valueChanges()
                .subscribe((result) => {
                    this.clearUserSessionTimeout();
    
                    if (result && result.sessionExpirationTime) {
                        this.setupSessionExpirationTime(firestoreUser, result.sessionExpirationTime);
                    }
                });
        }

        if (!this.ticketsViewSubscription) {
            this.ticketsViewSubscription = this.firestoreService
                .tenantDependentCollection(this.user.tenantId, 'configurations')
                .doc<any>('ticketViews')
                .valueChanges()
                .subscribe((ticketsView: any) => {
                    this.currentTicketsView = ticketsView && ticketsView.key 
                        ? ticketsView.key 
                        : 'escalatable';
                });
        }
    }

    /** used to create the 'showAfterLoginStatusPrompt' flag from projectId */
    private loginStatusPromptFlag(): string {
        return 'showAfterLoginStatusPrompt' + environment.firebaseConfig.projectId;
    }

    /** used to update the 'showAfterLoginStatusPrompt' flag */
    updateLoginStatusPrompt(showPrompt: boolean): void {
        if (this.currentTicketsView === 'non-escalatable') {
            return ;
        }

        this.showAfterLoginStatusPrompt = showPrompt;
        this.localStorage.set<boolean>(this.loginStatusPromptFlag(), this.showAfterLoginStatusPrompt, true);
        this.showAfterLoginStatusPromptSubject.next(this.showAfterLoginStatusPrompt);
    }

    /** used to fetch the 'showAfterLoginStatusPrompt' flag from local storage */
    private fetchLoginStatusPrompt(): void {
        this.showAfterLoginStatusPrompt = false;
        this.showAfterLoginStatusPrompt = this.localStorage.get<boolean>(this.loginStatusPromptFlag(), true) ?? this.showAfterLoginStatusPrompt;
        this.showAfterLoginStatusPromptSubject.next(this.showAfterLoginStatusPrompt);
    }

    /** whether or not should display the after login status prompt */
    loginStatusPromptObservable(): Observable<boolean> {
        return this.showAfterLoginStatusPromptSubject.asObservable();
    }

    /** Used to complete a unsuccessful authentication. */
    private authenticationUnsuccess(): void {
        this.updateLoginStatusPrompt(false);
        this.stopCurrentSessionUserUpdate();
        this._user = null;
        this.userSubject.next(this._user);
        this.isAuthenticatedSubject.next(false);
        this.ignoreUserActivity();
        this.stopConfigurationSubscription();
        this.stopTicketsViewSubscription();
        this.clearUserSessionTimeout();
    }

    /**
     * invoked to update the user status
     * @param userId the id of the user
     * @param newStatusId the status id
     * @param registerMetric if should register corresponding metric event
     */
    async updateUserStatus(userId: string, newStatusId: string, registerMetric: boolean): Promise<void> {
        if (this.currentTicketsView === 'non-escalatable') {
            return ;
        }

        try {
            const data = { tenantId: this.user.tenantId, userId, newStatusId };
            await this.firestoreService.cloudFunction('updateUserStatusId')(data);

            if (registerMetric) {
                await this.httpService.post<any>('/api/users/updateUserStatusId', data, this.httpRetryPolicyService.noRetryPolicy());
            }
        } catch (error) {}
    }

    /** Used to setup the user session expiration time from the last login and tenant configuration. */
    private setupSessionExpirationTime(firestoreUser: firebase.User, sessionExpirationTime: number): void {
        firestoreUser.getIdTokenResult().then((idTokenResult) => {
            const authTime = new Date(idTokenResult.authTime).getTime();
            const sessionDuration = sessionExpirationTime * 1000; // milliseconds
            const expiration = sessionDuration - (Date.now() - authTime);
            const timeout = expiration > 0 ? expiration : 0;
            this.userSessionTimeout = window.setTimeout(async () => {
                await this.logout(true);
            }, timeout);
        });
    }

    /** Stops the current authentication configuration subscription. */
    private stopConfigurationSubscription(): void {
        if (this.authenticationConfigurationSubscription) {
            this.authenticationConfigurationSubscription.unsubscribe();
            this.authenticationConfigurationSubscription = undefined;
        }
    }

    private stopTicketsViewSubscription(): void {
        if (this.ticketsViewSubscription) {
            this.ticketsViewSubscription.unsubscribe();
            this.ticketsViewSubscription = undefined;
        }
    }

    /** Clears the current user session timeout. */
    private clearUserSessionTimeout(): void {
        if (this.userSessionTimeout) {
            clearTimeout(this.userSessionTimeout);
            this.userSessionTimeout = undefined;
        }
    }

    /** Used to indicate when it is necessary to clear the data of the authenticated user. */
    shouldClearUserSession(): Observable<boolean> {
        return this.shouldClearUserSessionSubject.asObservable();
    }

    /**
     * The method responsible to render the recaptcha verifier
     * @param container is the div's id where this recaptcha will be rendered
     * @param size can be 'compact', 'normal' or 'invisible', the default value is 'invisible'.
     * @param theme can be 'dark' or 'light', the default value is 'light'.
     */
    async renderRecaptchaVerifier(container: string, size?: RecaptchaVerifierSize, theme?: RecaptchaVerifierTheme): Promise<void> {
        this.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(container, {
            size: !!size ? size : 'invisible',
            callback: (response) => {
                this.recaptchaVerifierStatusSubject.next(true);
            },
            'expired-callback': (expired) => {
                this.recaptchaVerifierStatusSubject.next(false);
                console.log(expired);
            },
            'error-callback': (error) => {
                this.recaptchaVerifierStatusSubject.next(false);
                console.log(error);
            },
            theme: !!theme ? theme : 'light',
        });

        try {
            await this.recaptchaVerifier.render();
        } catch (error) {
            console.log('recaptcha already rendered');
        }
    }

    /** The method responsible to clear the recaptcha verifier */
    clearRecaptchaVerifier(): void {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = undefined;
    }

    /** checks if the user is online, i.e., if there is activity less than 'isAvailableInterval' seconds ago */
    isUserAvailable(isAvailableInterval: number): boolean {
        return (
            this.isAuthenticated() && Math.floor(new Date().getTime() / 1000 - this._user.lastAccess.getTime() / 1000) < isAvailableInterval
        );
    }

    /**
     * Asynchronous operation authenticate the user.
     *
     * @throws LoginError in case of invalid credentials/unknown error.
     */
    async login(
        username: string,
        password: string,
        keepAuthenticated: boolean = true,
        validateCredentialsOnly: boolean = false
    ): Promise<SessionUser> {
        let sessionUser: SessionUser;

        try {
            if (validateCredentialsOnly) {
                await this.firestoreService.auth.signInWithEmailAndPassword(username, password);
            } else {
                const persistence = keepAuthenticated ? firebase.auth.Auth.Persistence.LOCAL : firebase.auth.Auth.Persistence.SESSION;
                await this.firestoreService.auth.setPersistence(persistence);
                const userCredentials = await this.firestoreService.auth.signInWithEmailAndPassword(username, password);
                sessionUser = await this.finishLoginSuccess(userCredentials, false);
            }
        } catch (error) {
            let code: LoginErrorCode;
            let phoneNumber: string;

            switch (error.code) {
                case 'auth/wrong-password':
                    code = 'wrongPassword';
                    break;
                case 'auth/user-not-found':
                    code = 'wrongUsername';
                    break;
                case 'auth/multi-factor-auth-required':
                    this.resolver = error.resolver;
                    const index = 0;

                    if (
                        this.resolver &&
                        this.resolver.hints.length > 0 &&
                        this.resolver.hints[index].factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID
                    ) {
                        const phoneInfoOptions = {
                            multiFactorHint: this.resolver.hints[index],
                            session: this.resolver.session,
                        };

                        phoneNumber = this.resolver.hints[index].phoneNumber;
                        this.phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
                        this.verificationId = await this.phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier);
                    }

                    code = 'multiFactorAuth';
                    break;
                default:
                    code = 'unknown';
                    break;
            }

            throw new LoginError(code, error.message, phoneNumber);
        }

        return sessionUser;
    }

    getMultifactorPhoneNumber(): string {
        const index = 0;

        if (
            this.resolver &&
            this.resolver.hints.length > 0 &&
            this.resolver.hints[index].factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID
        ) {
            const phoneNumber =
                this.resolver.hints[index].phoneNumber ?? this.localStorage.get<string>('wzcmYgJRgnSyHinHWOQOnEXiyxtDYogZBayNxjNl') ?? '';

            return phoneNumber;
        } else {
            const phoneNumber = this.localStorage.get<string>('wzcmYgJRgnSyHinHWOQOnEXiyxtDYogZBayNxjNl') ?? '';

            return phoneNumber;
        }
    }

    /** Asynchronous operation to send the verification code for the multi-factor authentication */
    async sendVerificationCodeAuth(): Promise<string> {
        if (this.multifactorAuthentication?.tenantWillUseGoogle2FA) {
            const index = 0;

            if (
                this.resolver &&
                this.resolver.hints.length > 0 &&
                this.resolver.hints[index].factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID
            ) {
                const phoneInfoOptions = {
                    multiFactorHint: this.resolver.hints[index],
                    session: this.resolver.session,
                };

                await this.phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier);
            }

            return '';
        } else {
            // invoke dashboard-rt-service 2FA endpoint 'send-code'
            const phoneNumber = this.multifactorAuthentication?.userPhone ?? this.getMultifactorPhoneNumber();
            const userTenant = this.multifactorAuthentication?.userTenantId;

            const response = await this.mfaAuthService.sendCode(userTenant, phoneNumber);
            return response?.status;
        }
    }

    /** Asynchronous operation to authenticate the user using multi-factor authentication */
    async loginMultiFactorAuth(verificationCode: string, keepAuthenticated: boolean = true): Promise<SessionUser> {
        let sessionUser = null;

        if (this.multifactorAuthentication === null || this.multifactorAuthentication === undefined) {
            this.multifactorAuthentication = this.localStorage.get<MultifactorAuthentication>('multifactorAuthenticationConfig', true);
        }

        if (this.multifactorAuthentication?.tenantWillUseGoogle2FA) {
            const credentials = firebase.auth.PhoneAuthProvider.credential(this.verificationId, verificationCode);
            const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credentials);
            const userCredentials: firebase.auth.UserCredential = await this.resolver.resolveSignIn(multiFactorAssertion);
            sessionUser = await this.finishLoginSuccess(userCredentials, true);
        } else {
            // invoke dashboard-rt-service 2FA endpoint 'validate-code'
            const phoneNumber = this.multifactorAuthentication?.userPhone ?? this.getMultifactorPhoneNumber();
            const userTenant = this.multifactorAuthentication?.userTenantId;

            try {
                const response = await this.mfaAuthService.validateCode(userTenant, verificationCode, phoneNumber);

                if (response?.status === 'approved') {
                    sessionUser = await this.finishLoginFromStorage(keepAuthenticated);
                } else {
                    throw new SMSTokenError('INVALID_TOKEN', 'The SMS code is invalid.');
                }
            } catch (error) {
                sessionUser = null;
                throw new SMSTokenError('EXPIRED_TOKEN', 'The SMS code has expired.');
            }
        }

        return sessionUser;
    }

    /** The method responsible for ending the login i.e. obtaining the sessionUser and indicating that the user is authenticated */
    async finishLoginFromStorage(keepAuthenticated: boolean = true): Promise<SessionUser> {
        let sessionUser = null;

        const userFromStorage = this.localStorage.get<string>('currentsessionuser', true);

        if (userFromStorage !== '') {
            const splittedCreds = atob(userFromStorage)?.split(';') ?? [];

            if (splittedCreds?.length === 2) {
                const username = atob(splittedCreds[0]);
                const password = atob(splittedCreds[1]);

                try {
                    this.localStorage.remove('currentsessionuser', true);
                    this.localStorage.remove('wzcmYgJRgnSyHinHWOQOnEXiyxtDYogZBayNxjNl');
                    this.localStorage.remove('keepAuthenticated', true);
                    this.localStorage.remove('multifactorAuthenticationConfig', true);
                } catch (e) {
                    throw new Error(e?.message);
                }

                const persistence = keepAuthenticated ? firebase.auth.Auth.Persistence.LOCAL : firebase.auth.Auth.Persistence.SESSION;
                await this.firestoreService.auth.setPersistence(persistence);
                const userCredentials = await this.firestoreService.auth.signInWithEmailAndPassword(username, password);
                sessionUser = await this.finishLoginSuccess(userCredentials, true);
            }
        }

        return sessionUser;
    }

    async fetchTicketsViewConfiguration(tenantId: string): Promise<any> {
        const snapshot = await this.firestoreService
            .tenantDependentCollection(tenantId, 'configurations')
            .doc<any>('ticketViews')
            .ref
            .get();
        
        if (!snapshot.exists) {
            return 'escalatable';
        }

        const data = snapshot.data();
        const ticketsView = data && data.key 
            ? data.key 
            : 'escalatable';
        
        return ticketsView;
    }

    /** The method responsible for ending the login i.e. obtaining the sessionUser and indicating that the user is authenticated */
    private async finishLoginSuccess(userCredentials: any, isMFAEnabled: boolean = false): Promise<SessionUser> {
        const sessionUser = await this.getSessionUser(userCredentials.user);
        this.currentTicketsView = await this.fetchTicketsViewConfiguration(sessionUser.tenantId);

        this.isLogin = true;
        this.hasVerified = true;

        if (!isMFAEnabled) {
            this._user = sessionUser;
            this.userSubject.next(this._user);
            this.isAuthenticatedSubject.next(true);
        }

        this.authenticationSuccess(sessionUser, userCredentials.user);

        try {
            this.localStorage.set<boolean>(this.hasVerifiedFlag(), this.hasVerified, true);
            this.localStorage.remove('keepAuthenticated', true);
            this.localStorage.remove('multifactorAuthenticationConfig', true);
        } catch (e) {
            throw new Error(e?.message);
        }

        return sessionUser;
    }

    /** Asynchronous operation to logout the current authenticated user. */
    async logout(releaseTicketsFromOperator: boolean = false): Promise<any> {
        if (releaseTicketsFromOperator) {
            this.shouldClearUserSessionSubject.next(releaseTicketsFromOperator);
        } else {
            this.finishLogout();
        }
    }

    /** Used to finish the logout of the current authenticated user. */
    async finishLogout(): Promise<any> {
        this.hasVerified = false;

        try {
            this.localStorage.set<boolean>(this.hasVerifiedFlag(), this.hasVerified, true);

            this.localStorage.remove('currentsessionuser', true);
            this.localStorage.remove('wzcmYgJRgnSyHinHWOQOnEXiyxtDYogZBayNxjNl');
            this.localStorage.remove('keepAuthenticated', true);
            this.localStorage.remove('multifactorAuthenticationConfig', true);
        } catch (e) {
            throw new Error(e?.message);
        }

        if (!(await this.isAuthenticated())) {
            return;
        }

        await this.updateUserStatus(this.user.id, environment.operatorLoggedOutStatuses, !this.user?.shouldNotRegisterMetrics);
        await this.firestoreService.auth.signOut();
        this.authenticationUnsuccess();

        try {
            // delete schedule context from local storage
            localStorage.removeItem(btoa('@pts_c#ntx'));
        } catch (e) {
            throw new Error(e?.message);
        }

        // navigate this way instead of 'router.navigate' to reload the app, resetting the app state
        window.location.href = '/authentication/login';
    }

    /**
     * The method responsible for checking whether the tenant uses 2FA and whether the user has a phoneNumber
     * @param email the email of the user to be verified
     */
    async useMultifactorAuthentication(email: string): Promise<MultifactorAuthentication> {
        const useMultifactorAuthenticationCallable = this.firestoreService.cloudFunction('useMultifactorAuthentication');
        const result = await useMultifactorAuthenticationCallable({ email, excludeDeleted: true });

        this.multifactorAuthentication = {
            tenantHasMultifactorAuthenticationEnabled: result.data.tenantHasMultifactorAuthenticationEnabled,
            tenantWillUseGoogle2FA: result.data.tenantWillUseGoogle2FA,
            userPhone: result.data.userPhone,
            userHasPhone: result.data.userHasPhone,
            userTenantId: result.data.userTenantId,
        };

        try {
            this.localStorage.set<MultifactorAuthentication>('multifactorAuthenticationConfig', this.multifactorAuthentication, true);
        } catch (e) {
            throw new Error(e?.message);
        }

        return this.multifactorAuthentication;
    }

    /** Asynchronous operation to indicate if the user is currently authenticated.  */
    async isAuthenticated(): Promise<boolean> {
        return new Promise<boolean>((result) => {
            const sub = this.isAuthenticatedSubject.asObservable().subscribe((res) => {
                result(res);
                setTimeout(() => sub.unsubscribe());
            });
        });
    }

    /** Asynchronous operation to indicate if is required to change the password of the currently authenticated user */
    async passwordChangeRequired(): Promise<boolean> {
        if (!this.user) {
            throw new Error('This operation requires the user to be authenticated');
        }

        const userSnapshot = await this._usersCollection.doc(this.user.id).ref.get();

        if (!userSnapshot.exists) {
            throw new Error('The user does not exists');
        }

        return userSnapshot.data().passwordChangeRequired || false;
    }

    /** Asynchronous operation to change the user password. */
    async changePassword(currentPassword: string, newPassword: string, newConfirmationPassword: string): Promise<void> {
        if (!this.user) {
            throw new Error('This operation requires the user to be authenticated');
        }

        let userCredential: firebase.auth.UserCredential;

        // perform authentication of the current user to verify if the current password is correct
        try {
            userCredential = await this.firestoreService.auth.signInWithEmailAndPassword(this.user.email, currentPassword);
        } catch (error) {
            throw new ChangePasswordError('invalidPassword', 'The current password is invalid');
        }

        // update password
        const changeUserPassword = this.firestoreService.cloudFunction('Users_ChangeUserPassword');
        const res = await changeUserPassword({ userId: this.user.id, newPassword, newConfirmationPassword });

        if (res && res.data) {
            const { data } = res;

            switch (data.code) {
                case 'invalid-argument': {
                    switch (data.details) {
                        case 'confirmation-password-dont-match-password':
                            throw new ChangePasswordError(
                                'newAndConfirmationPasswordDoNotMatch',
                                'The new and confirmation password are not equal'
                            );
                        case 'password-insecure':
                            throw new ChangePasswordError(
                                'passwordInsecure',
                                "the password don't have the necessary length/chars to be considered secure"
                            );
                        case 'password-already-used':
                            throw new ChangePasswordError('passwordAlreadyUsed', 'the password has already been used by the user');
                        case 'password-contains-used-data':
                            throw new ChangePasswordError(
                                'passwordContainsUserData',
                                'the password contains segments that match some user data (name / email)'
                            );
                    }
                    break;
                }
                case 'permission-denied':
                    throw new ChangePasswordError('permissionDenied', 'Only the user can change it own password');
                case 'unknown': // 'unknown' => unknown unexpected error
                    throw new ChangePasswordError('unknown', 'The current password is invalid');
            }
        }
    }

    /** Asynchronous operation to reset a user password using the given token */
    async resetPassword(token: string, password: string, confirmationPassword: string): Promise<void> {
        // reset be password for the user associated with the provided token
        const resetPassword = this.firestoreService.cloudFunction('Users_ResetPassword');
        const res = await resetPassword({ token, password, confirmationPassword });

        // case the response data has "data", an error ocurred and the error code is provided at the "data.code"
        if (res && res.data) {
            const { data } = res;

            switch (data.code) {
                case 'deadline-exceeded': // 'deadline-exceeded' => token has expired
                    throw new ChangePasswordError('tokenExpired', 'The token has expired.');
                case 'invalid-argument': {
                    switch (data.details) {
                        case 'confirmation-password-dont-match-password':
                            throw new ChangePasswordError(
                                'newAndConfirmationPasswordDoNotMatch',
                                'The new and confirmation password are not equal'
                            );
                        case 'password-insecure':
                            throw new ChangePasswordError(
                                'passwordInsecure',
                                "the password don't have the necessary length/chars to be considered secure"
                            );
                        case 'password-already-used':
                            throw new ChangePasswordError('passwordAlreadyUsed', 'the password has already been used by the user');
                        case 'password-contains-used-data':
                            throw new ChangePasswordError(
                                'passwordContainsUserData',
                                'the password contains segments that match some user data (name / email)'
                            );
                    }
                    break;
                }
                case 'unknown': // 'unknown' => unknown unexpected error
                    throw new ChangePasswordError('unknown', 'The current password is invalid');
            }
        }
    }

    /**
     * Asynchronous operation to request a user password reset by sending a reset link to the user's associated e-mail.
     */
    async resetPasswordRequest(email: string): Promise<any> {
        const response = await this.firestoreService.cloudFunction('Users_RequestPasswordReset')({ email });

        if (response && response.data && response.data.code) {
            switch (response.data.code) {
                case 'not-found':
                    throw new ResetPasswordError('NOT_FOUND', 'Account not found, please contact your administrator.');
                default:
                    throw new ResetPasswordError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    /** Asynchronous operation to resend a confirmation email to the registered e-mail. */
    async resendConfirmationEmail(email: string): Promise<void> {
        const response = await this.firestoreService.cloudFunction('usersRequestAccountConfirmation')({ email });

        if (response && response.data && response.data.code) {
            switch (response.data.code) {
                case 'not-found':
                    throw new ResendConfirmationEmailError('NOT_FOUND', 'Account not found, please contact your administrator.');
                default:
                    throw new ResendConfirmationEmailError('UNKNOWN', 'An unknown error occurred.');
            }
        }
    }

    /** Asynchronous operation to verify reset password token validity. */
    async verifyResetPasswordTokenValidity(token: string): Promise<TokenValidity> {
        const verifyRequestPasswordResetValidity = this.firestoreService.cloudFunction('Users_VerifyRequestPasswordResetValidity');
        const res = await verifyRequestPasswordResetValidity({ token });

        if (res && res.data) {
            const { data } = res;

            // verifies if it is valid
            if (data.valid) {
                return { valid: true, user: data.user };
            }

            // otherwise is invalid
            // return the right error
            if (data.error === 'Expired') {
                return {
                    valid: false,
                    error: 'Expired',
                };
            }

            if (data.error === 'NotFound') {
                return {
                    valid: false,
                    error: 'Invalid',
                };
            }
        }

        return {
            valid: false,
            error: 'Invalid',
        };
    }

    async getSessionTokens(): Promise<string> {
        // get JWT token used to identify current user, If it has expired it'll generate a new one.
        const userIdToken = await this._fbUser.getIdToken();

        return userIdToken;
    }

    private async getSessionUser(fbUser: firebase.User): Promise<SessionUser> {
        // get user data
        const userSnapshot = await this._usersCollection.doc<SessionUserDTO>(fbUser.uid).ref.get();

        if (!userSnapshot.exists) {
            return null;
        }

        this._fbUser = fbUser;

        const userDTO = userSnapshot.data() as SessionUserDTO;
        userDTO.id = userSnapshot.id;
        userDTO.getSessionToken = () => this.getSessionTokens();

        return await this.sessionUserDTO2SessionUser(userDTO);
    }

    private startSessionUserUpdate(fbUser: firebase.User): void {
        if (!this.startSessionUserUpdateSubscription) {
            this.startSessionUserUpdateSubscription = this._usersCollection
                .doc(fbUser.uid)
                .snapshotChanges()
                .subscribe(async (userDoc) => {
                    const userDTO = userDoc.payload.data() as SessionUserDTO;
                    userDTO.id = userDoc.payload.id;
    
                    this._fbUser = fbUser;
    
                    userDTO.getSessionToken = () => this.getSessionTokens();
    
                    this._user = await this.sessionUserDTO2SessionUser(userDTO);
                    this.userSubject.next(this._user);
                });
        }
    }

    private stopCurrentSessionUserUpdate(): void {
        if (this.startSessionUserUpdateSubscription) {
            this.startSessionUserUpdateSubscription.unsubscribe();
            this.startSessionUserUpdateSubscription = null;
        }
    }

    private async sessionUserDTO2SessionUser(dto: SessionUserDTO): Promise<SessionUser> {
        // get user role data
        const roleSnapshot = await this.firestoreService.collection<Role>(`tenants/${dto.tenantId}/roles`).doc(dto.roleId).ref.get();

        if (!roleSnapshot.exists) {
            return;
        }

        const role = roleSnapshot.data() as Role;
        role.id = roleSnapshot.id;
        
        return {
            canTakeControlOfEscalatedTickets: dto.canTakeControlOfEscalatedTickets ?? false,
            createdAt: dto.createdAt.toDate(),
            deleted: dto.deleted ?? false,
            email: dto.email,
            getSessionToken: dto.getSessionToken,
            id: dto.id,
            inviteAccepted: dto.inviteAccepted,
            isInternal: dto.isInternal ?? false,
            lastAccess: dto.lastAccess ? dto.lastAccess.toDate() : null,
            name: dto.name,
            passwordChangeRequired: dto.passwordChangeRequired,
            phone: dto?.phone,
            photo: dto.photo,
            role,
            statusId: dto?.statusId ? dto.statusId : environment.operatorLoggedInStatuses,
            tagsIds: dto.tagsIds,
            tenantId: dto.tenantId,
            shouldNotRegisterMetrics: dto?.shouldNotRegisterMetrics ?? false,
        };
    }

    private ignoreUserActivity(): void {
        window.document.removeEventListener('keyup', this.onKeyboardEvent);
        window.document.removeEventListener('mousemove', this.onMouseEvent);

        if (this.userActivitySubscription) {
            this.userActivitySubscription.unsubscribe();
        }
    }

    private onKeyboardEvent($event: KeyboardEvent): void {
        this.keyboardSubject.next($event);
    }

    private onMouseEvent($event: MouseEvent): void {
        this.mouseSubject.next($event);
    }

    private watchUserActivity(): void {
        if (!this.userActivitySubscription) {
            this.ngZone.runOutsideAngular(() => {
                window.document.addEventListener('keyup', this.onKeyboardEvent.bind(this));
                window.document.addEventListener('mousemove', this.onMouseEvent.bind(this));
            });

            this.userActivitySubscription = combineLatest([this.keyboardSubject.asObservable(), this.mouseSubject.asObservable()])
                .pipe(throttleTime(60000), distinctUntilChanged())
                .subscribe(() => {
                    if (this._user) {
                        this.firestoreService.cloudFunction('updateUserLastAccess')({ userId: this._user.id });

                        if (!this.isFirstLoading) {
                            this.updateUserStatusActivityDetected(this.router.url);
                        } else {
                            this.isFirstLoading = false;
                        }
                    }
                });
        }
    }

    private updateUserStatusActivityDetected(url: string): void {
        if (!url) {
            return ;
        }

        if (
            !this.isLogin && 
            !this.showAfterLoginStatusPrompt && 
            this._user.statusId === environment.operatorUnavailableStatuses
        ) {
            this.updateLoginStatusPrompt(true);
        } else if (
            !this.isLogin && 
            !this.showAfterLoginStatusPrompt && 
            url.includes('tickets') && 
            [
                environment.operatorLoggedInStatuses,
            ].includes(this._user.statusId)
        ) {
            this.updateLoginStatusPrompt(true);
        } else if (
            !this.isLogin && 
            !this.showAfterLoginStatusPrompt && 
            !url.includes('tickets') && 
            [
                environment.operatorAvailableStatuses, 
                environment.operatorAvailableOutboundStatuses,
            ].includes(this._user.statusId)
        ) {
            this.updateUserStatus(this._user.id, environment.operatorLoggedInStatuses, !this._user?.shouldNotRegisterMetrics);
        }
    }
}
